import { motion } from 'framer-motion';
import { Building2, Hotel, PartyPopper, UtensilsCrossed, School, Car, Check, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';

const Services = () => {
  const services = [
    {
      icon: Building2,
      title: 'Mercado Imobiliário',
      description: 'Visitas virtuais que vendem antes mesmo da chave girar. Incorporadoras, imobiliárias e corretores ganham uma vitrine aberta 24 horas para o mundo.',
      benefits: [
        'Apartamento decorado disponível virtualmente mesmo após entrega da obra',
        'Estande de vendas digital que nunca fecha',
        'Investidores de qualquer lugar acessam seu empreendimento',
        'Redução drástica de visitas presenciais improdutivas',
        'Leads mais qualificados e prontos para fechar negócio',
        'Aumento comprovado na taxa de conversão',
      ],
      image: 'https://images.unsplash.com/photo-1758957530781-4ff54e09bee2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMHBhbm9yYW1pYyUyMHZpZXd8ZW58MHx8fHwxNzc1MjM4MjI1fDA&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: Hotel,
      title: 'Hotéis e Pousadas',
      description: 'Transforme curiosidade em reservas. Mostre cada canto das acomodações com a riqueza de detalhes que só o 360° oferece.',
      benefits: [
        'Quartos e suítes apresentados em todos os ângulos',
        'Áreas de lazer, piscina e restaurante em imersão total',
        'Hóspedes reservam com mais confiança e menos cancelamentos',
        'Destaque em plataformas como Booking e Airbnb',
        'Marketing visual que trabalha enquanto você descansa',
        'Diferencial competitivo frente a concorrentes com fotos estáticas',
      ],
      image: 'https://images.pexels.com/photos/17926426/pexels-photo-17926426.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    {
      icon: PartyPopper,
      title: 'Salões de Festas e Eventos',
      description: 'Seu espaço apresentado com a grandiosidade que ele merece. O cliente se encanta antes mesmo de visitar presencialmente.',
      benefits: [
        'Visualização completa do layout e dimensões reais',
        'Planejamento de decoração facilitado para clientes e decoradores',
        'Mais agendamentos com menos visitas presenciais',
        'Link compartilhável para cônjuges, familiares e colegas de decisão',
        'Espaço disponível para visitação virtual 24/7',
        'Posicionamento premium frente à concorrência',
      ],
      image: 'https://images.unsplash.com/photo-1762765685319-fdaf8d22085d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxldmVudCUyMHZlbnVlJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc1MjMwMTY2fDA&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: UtensilsCrossed,
      title: 'Restaurantes',
      description: 'A atmosfera do seu restaurante sentida pela tela. Conquiste clientes pelo ambiente antes mesmo do primeiro prato.',
      benefits: [
        'Ambiente e decoração apresentados em sua totalidade',
        'Cliente escolhe a mesa ideal para ocasiões especiais',
        'Espaços privativos visualizados antes da reserva',
        'Integração direta com Google Maps e Google Business',
        'Mais reservas e clientes mais satisfeitos',
        'Presença digital que impressiona e diferencia',
      ],
      image: 'https://images.unsplash.com/photo-1771574207939-ba1364dcdb21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjByZXN0YXVyYW50JTIwaW50ZXJpb3IlMjBlbGVnYW50fGVufDB8fHx8MTc3NTIzODIyNXww&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: School,
      title: 'Escolas',
      description: 'A infraestrutura da sua escola apresentada com sofisticação. Conquiste pais e alunos com um tour imersivo pelas instalações antes mesmo da visita presencial.',
      benefits: [
        'Salas de aula, laboratórios e quadras apresentados com detalhes em 360°',
        'Visitação virtual para pais que não têm tempo de ir presencialmente',
        'Segurança e transparência fortalecidas na decisão de matrícula',
        'Destaque inovador frente a outras instituições de ensino',
        'Redução de tempo e equipe gasta com visitas de apresentação',
        'Imagem educacional moderna e alinhada à tecnologia',
      ],
      image: '/images/modern-classroom.png',
    },
    {
      icon: Car,
      title: 'Concessionárias de Veículos',
      description: 'O comprador dentro do carro antes mesmo de pisar no showroom. Tours 360° que colocam o cliente no banco do motorista e aceleram a decisão de compra.',
      benefits: [
        'Interior do veículo explorado em 360° com riqueza de detalhes',
        'Hotspots interativos destacando tecnologia, acabamento e diferenciais',
        'Showroom virtual com toda a linha de modelos disponível',
        'Cliente mais seguro e preparado para a negociação presencial',
        'Alcance de compradores em outras cidades e estados',
        'Experiência inovadora que posiciona sua concessionária à frente do mercado',
      ],
      image: 'https://images.unsplash.com/photo-1580273916550-e323be2ae537?auto=format&fit=crop&w=800&q=80',
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-32 pb-24" data-testid="services-page">
      <SEO 
        title="Serviços de Tours Virtuais 360° | Gran Tour 360"
        description="Tours virtuais 360° para imobiliárias, hotéis, salões de festas, restaurantes, escolas e concessionárias. Soluções sob medida para cada segmento."
        url="https://grantour360.com.br/servicos"
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[#FF3366] text-xs uppercase tracking-[0.2em] font-semibold mb-4">Serviços</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
              Cada Negócio, Uma Solução Exclusiva
            </h1>
            <p className="text-gray-500 max-w-3xl mx-auto text-base sm:text-lg leading-relaxed">
              Tours virtuais 360° desenvolvidos com visão comercial, projetados para destacar o que realmente importa em cada segmento.
            </p>
          </motion.div>
        </div>

        <div className="space-y-24">
          {services.map((service, index) => {
            const Icon = service.icon;
            const isEven = index % 2 === 0;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  isEven ? '' : 'lg:grid-flow-dense'
                }`}
                data-testid={`service-${index}`}
              >
                <div className={isEven ? '' : 'lg:col-start-2'}>
                  <div className="w-16 h-16 bg-[#FF3366]/10 rounded-xl flex items-center justify-center mb-6">
                    <Icon size={32} className="text-[#FF3366]" strokeWidth={1.5} />
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
                    {service.title}
                  </h2>
                  <p className="text-gray-500 text-base sm:text-lg mb-8 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-3">
                    {service.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-[#FF3366]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check size={14} className="text-[#FF3366]" strokeWidth={3} />
                        </div>
                        <span className="text-gray-600">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={isEven ? 'lg:col-start-2' : 'lg:col-start-1 lg:row-start-1'}>
                  <div className="rounded-lg overflow-hidden shadow-lg">
                    <img
                      src={service.image}
                      alt={`Tour virtual 360° para ${service.title}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-24 text-center">
          <div className="bg-gray-50 rounded-xl p-12 border border-gray-100">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 tracking-tight">
              Vamos Dar Vida ao Seu Espaço?
            </h2>
            <p className="text-gray-500 text-base sm:text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
              Conte-nos sobre o seu projeto e receba uma proposta personalizada. O primeiro passo para transformar como seus clientes veem o seu negócio.
            </p>
            <Link
              to="/contato"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#FF3366] text-white font-semibold rounded-md hover:bg-[#FF5C85] transition-all"
              data-testid="services-cta"
            >
              Solicitar Orçamento
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;
